import React, { useState } from "react";
import { useData } from "data/data-provider";

interface IconProps {
  fill?: string;
}

const PlusIcon: React.FC<IconProps> = ({ fill }) => (
  <svg
    width="12"
    height="13"
    viewBox="0 0 12 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.16666 5.66675V0.666748H6.83333V5.66675H11.8333V7.33341H6.83333V12.3334H5.16666V7.33341H0.166664V5.66675H5.16666Z"
      fill={fill}
      fillOpacity="0.8"
    />
  </svg>
);
const RemoveIcon: React.FC<IconProps> = ({ fill }) => (
  <svg
    width="12"
    height="3"
    viewBox="0 0 12 3"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.166664 0.666748V2.33341H11.8333V0.666748H0.166664Z"
      fill={fill}
    />
  </svg>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

const Section: React.FC<SectionProps> = ({
  title,
  children,
  isOpen,
  onToggle,
}) => {
  const { companyDetails } = useData();
  return (
    <div className=" lg:my-4 rounded-md">
      <button
        onClick={onToggle}
        className="flex justify-between w-full space-x-4  py-6 text-left items-center"
      >
        {/* `title` arrives as a Quill-generated HTML string (typically wrapped
            in `<p>…</p>` with optional `<strong>` / `<em>`). Render via
            dangerouslySetInnerHTML so the tags are parsed instead of shown
            literally. Same fix is already in DesignB's RentalPolicies. */}
        <span
          className="text-lg font-semibold leading-7 text-left text-text-color [&_p]:m-0 [&_p]:inline"
          dangerouslySetInnerHTML={{ __html: title || "" }}
        />
        <span>
          {isOpen ? (
            <RemoveIcon fill={companyDetails?.text_color} />
          ) : (
            <PlusIcon fill={companyDetails?.text_color} />
          )}
        </span>
      </button>
      {isOpen && <div className="px-4 py-2">{children}</div>}
    </div>
  );
};

const FAQ: React.FC = () => {
  const { faqs } = useData();
  const [openSectionIndex, setOpenSectionIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setOpenSectionIndex(openSectionIndex === index ? null : index);
  };

  // Calculate midpoint to split FAQs into two columns
  const midpoint = Math.ceil((faqs?.keypoints?.length || 0) / 2);

  return (
    <>
      {/* All four FMS-managed strings (section heading, description, each
          question, each answer) come back as Quill HTML. They all need to
          go through dangerouslySetInnerHTML so the `<p>` / `<strong>` /
          `<em>` tags render instead of showing as literal text. */}
      <div className="justify-center mb-8 text-center">
        <h2
          className="text-3xl md:text-4xl font-semibold [&_p]:m-0 [&_p]:inline"
          dangerouslySetInnerHTML={{ __html: faqs?.heading || "" }}
        />
        <p
          className="[&_p]:m-0 [&_p]:inline"
          dangerouslySetInnerHTML={{ __html: faqs?.description || "" }}
        />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 items-start lg:gap-14">
        {/* First column */}
        <div className="">
          {faqs?.keypoints?.slice(0, midpoint).map((item: any, index: any) => (
            <Section
              key={index}
              title={item?.heading}
              isOpen={openSectionIndex === index}
              onToggle={() => handleToggle(index)}
            >
              <span
                className="block text-base font-normal leading-6 text-left text-text-color
                  [&_p]:mb-2 last:[&_p]:mb-0
                  [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:mb-1
                  [&_strong]:font-semibold [&_em]:italic [&_u]:underline"
                dangerouslySetInnerHTML={{ __html: item?.description || "" }}
              />
            </Section>
          ))}
        </div>

        {/* Second column */}
        <div className="">
          {faqs?.keypoints?.slice(midpoint).map((item: any, index: any) => (
            <Section
              key={index + midpoint}
              title={item?.heading}
              isOpen={openSectionIndex === index + midpoint}
              onToggle={() => handleToggle(index + midpoint)}
            >
              <span
                className="block text-base font-normal leading-6 text-left text-text-color
                  [&_p]:mb-2 last:[&_p]:mb-0
                  [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:mb-1
                  [&_strong]:font-semibold [&_em]:italic [&_u]:underline"
                dangerouslySetInnerHTML={{ __html: item?.description || "" }}
              />
            </Section>
          ))}
        </div>
      </div>
    </>
  );
};

export default FAQ;
